import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Icon } from '../Icons';
import { Portal } from '../Portal';
import styles from './modal.module.scss';

interface ModalProps {
  children: React.ReactNode;
  modalClassName?: string;
  overlayClassName?: string;
  isOpen?: boolean;
  onRequestClose?: (event?: React.MouseEvent | React.KeyboardEvent) => void;
  modalFooter?: () => React.ReactNode;
  withCloseIcon?: boolean;
  canCloseOutside?: boolean;
}

export const Modal = React.memo<ModalProps>(
  ({
    children,
    modalClassName,
    overlayClassName,
    isOpen = false,
    onRequestClose = () => undefined,
    modalFooter = () => null,
    withCloseIcon = true,
    canCloseOutside = true,
  }) => {
    const [isClickInside, setIsClickInside] = useState(false);
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      const scrollY = window.scrollY; // Сохраняем позицию скролла

      const lockScroll = () => {
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.width = '100%';
      };

      const unlockScroll = () => {
        document.body.style.overflow = originalStyle;
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };

      const preventTouchMove = (e: any) => {
        if (modalRef.current && modalRef.current.contains(e.target)) {
          return; // Разрешаем прокрутку внутри модального окна
        }
        e.preventDefault();
      };

      if (isOpen) {
        lockScroll();
        document.addEventListener('touchmove', preventTouchMove, { passive: false });
      } else {
        unlockScroll();
      }

      return () => {
        if (isOpen) {
          unlockScroll();
        }
        document.removeEventListener('touchmove', preventTouchMove);
      };
    }, [isOpen, modalRef]);

    return (
      <Portal>
        <section
          ref={modalRef}
          className={cx(
            styles.overlay,
            { [styles.overlayOpen]: isOpen },
            overlayClassName,
          )}
          onMouseDown={() => {
            if (!canCloseOutside) {
              return;
            }
            setIsClickInside(false);
          }}
          onMouseUp={() => {
            if (canCloseOutside && !isClickInside) {
              onRequestClose();
            }
          }}
        >
          <dialog
            className={cx(
              styles.modal,
              { [styles.modalOpen]: isOpen },
              modalClassName,
            )}
            open={isOpen}
            onMouseDown={event => {
              event.stopPropagation();
              setIsClickInside(true);
            }}
            onMouseUp={event => {
              event.stopPropagation();
              setIsClickInside(false);
            }}
            onClick={event => event.stopPropagation()}
          >
            {children}
            {withCloseIcon ? (
              <Icon.Rounded
                className={styles.closeIcon}
                Icon={Icon.Close}
                onClick={onRequestClose}
              />
            ) : null}
            {modalFooter()}
          </dialog>
        </section>
      </Portal>
    );
  },
);
